import {
	Elements,
	PaymentElement,
	useElements,
	useStripe,
} from "@stripe/react-stripe-js";
import type { Stripe, StripeElements } from "@stripe/stripe-js";
import { useMemo, useState } from "react";
import { usePausePosthog } from "@/hooks/use-pause-posthog";
import { getAppearance, getStripePromise } from "@/util/stripe";

interface StripePaymentFormProps {
	/** Stripe client secret for the PaymentIntent or SetupIntent. */
	clientSecret: string;
	/** Called on form submit with Stripe instances. Return an error message string to display, or undefined on success. */
	onSubmit: (ctx: {
		stripe: Stripe;
		elements: StripeElements;
	}) => Promise<string | undefined>;
	/** Render prop for full layout control over the payment element, error, and buttons. */
	children: (props: {
		paymentElement: React.ReactNode;
		error: React.ReactNode | null;
		isProcessing: boolean;
		isReady: boolean;
	}) => React.ReactNode;
	className?: string;
}

/**
 * Shared Stripe payment form that wraps Elements provider, memoizes appearance,
 * and manages processing/error state. Uses a render prop for layout flexibility.
 */
export function StripePaymentForm({
	clientSecret,
	onSubmit,
	children,
	className,
}: StripePaymentFormProps) {
	const appearance = useMemo(() => getAppearance(), []);

	return (
		<Elements
			stripe={getStripePromise()}
			options={{ clientSecret, appearance }}
		>
			<InnerForm onSubmit={onSubmit} className={className}>
				{children}
			</InnerForm>
		</Elements>
	);
}

function InnerForm({
	onSubmit,
	children,
	className,
}: Omit<StripePaymentFormProps, "clientSecret">) {
	const stripe = useStripe();
	const elements = useElements();
	const [isProcessing, setIsProcessing] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);

	// Pause PostHog session recording to prevent performance issues with Stripe iframes
	usePausePosthog();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!stripe || !elements) return;

		setIsProcessing(true);
		setErrorMessage(null);

		const error = await onSubmit({ stripe, elements });
		if (error) {
			setErrorMessage(error);
			setIsProcessing(false);
		}
	};

	const paymentElement = <PaymentElement />;
	const errorElement = errorMessage ? (
		<p className="text-sm text-error">{errorMessage}</p>
	) : null;

	return (
		<form onSubmit={handleSubmit} className={className}>
			{children({
				paymentElement,
				error: errorElement,
				isProcessing,
				isReady: !!stripe,
			})}
		</form>
	);
}
